import { useEffect, useRef } from "react";
import type { MediaElement, Page } from "@deckloom/core/schema";
import { walkElements } from "@deckloom/core/timeline";
import { elementBBox } from "@deckloom/core/geometry";
import { usePlayer } from "./context";
import type { MediaController } from "./engine/MediaController";

export interface MediaLayerProps {
  page: Page;
  controller: MediaController;
  /** show native controls (presenter) */
  controls: boolean;
  /** fired when the user taps a media element (onClickShape start mode) */
  onToggle?: (elementId: string) => void;
}

function MediaItem({ el, page, controller, controls, onToggle }: { el: MediaElement } & MediaLayerProps) {
  const { deck, mediaUrl, playUrl } = usePlayer();
  const ref = useRef<HTMLMediaElement>(null);
  const bbox = elementBBox(page.elements, el.id) ?? { x: el.transform.x, y: el.transform.y, w: el.transform.w, h: el.transform.h };
  const style = {
    position: "absolute" as const,
    left: `${(bbox.x / deck.size.w) * 100}%`,
    top: `${(bbox.y / deck.size.h) * 100}%`,
    width: `${(bbox.w / deck.size.w) * 100}%`,
    height: `${(bbox.h / deck.size.h) * 100}%`,
    transformOrigin: "center",
  };

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    controller.attach(el.id, node);
    return () => controller.detach(el.id);
  }, [controller, el.id]);

  const src = el.source ? playUrl(el.source.mediaId) : undefined;
  const poster = el.poster ? mediaUrl(el.poster) : undefined;
  const pending = el.source?.playable === "afterTranscode";
  const clickable = el.startMode === "onClickShape" || el.startMode === "manual";

  return (
    <div className="anim media" data-el={el.id} style={style} onClick={clickable ? () => onToggle?.(el.id) : undefined}>
      {el.mediaType === "video" ? (
        <video
          ref={ref as React.RefObject<HTMLVideoElement>}
          src={src}
          poster={poster}
          playsInline
          preload="metadata"
          controls={controls}
          style={{ width: "100%", height: "100%", objectFit: "fill", background: poster ? "transparent" : "#000", display: "block" }}
        />
      ) : (
        <>
          {poster && <img src={poster} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} draggable={false} />}
          <audio ref={ref as React.RefObject<HTMLAudioElement>} src={src} preload="metadata" controls={controls} style={{ position: "absolute", left: 0, bottom: 0, width: "100%", maxHeight: "40px" }} />
        </>
      )}
      {pending && (
        <div style={{ position: "absolute", top: 8, left: 8, background: "rgba(0,0,0,.6)", color: "#fff", fontSize: 12, padding: "2px 6px", borderRadius: 4, fontFamily: "sans-serif" }}>
          processing…
        </div>
      )}
    </div>
  );
}

/** HTML layer above the slide SVG that hosts <video>/<audio> at their slide positions. */
export function MediaLayer(props: MediaLayerProps) {
  const items: MediaElement[] = [];
  for (const e of walkElements(props.page.elements)) if (e.kind === "media") items.push(e);
  if (items.length === 0) return null;
  return (
    <div className="media-layer" style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
      {items.map((el) => (
        <div key={el.id} style={{ pointerEvents: "auto", position: "absolute", inset: 0 }}>
          <MediaItem el={el} {...props} />
        </div>
      ))}
    </div>
  );
}
